import {createRouter, createWebHistory} from 'vue-router';
import AboutView from '@/views/AboutView.vue';
import FileUploadView from "@/views/FileUploadView.vue";
import FileDetailView from "@/views/FileDetailView.vue";
import AdminView from "@/views/AdminView.vue";
import HomeView_new from "@/views/HomeView.vue";
import ProfileView from "@/views/ProfileView.vue";
import NotAuthorizedView from "@/views/NotAuthorizedView.vue";
import LoginView from "@/views/LoginView.vue";
import FourZeroFourView from "@/views/FourZeroFourView.vue";
import TermsOfServiceView from "@/views/TermsOfServiceView.vue";
import store from '@/store';
import {authReadyPromise} from '@/utils/authReady';
import ChangePasswordView from "@/views/ChangePasswordView.vue";
import PasswordResetVue from "@/views/ForcedPasswordResetView.vue";
import ConfirmSignupCodeView from "@/views/ConfirmSignupCodeView.vue";
import ForgotPasswordView from "@/views/ForgotPasswordView.vue";
import ShortLinkView from "@/views/ShortLinkView.vue";
import LinkDetailView from "@/views/LinkDetailView.vue";
import ShortenView from "@/views/ShortenView.vue";
import PasteView from "@/views/PasteView.vue";

const routes = [
    {
        path: '/',
        name: "Home",
        component: HomeView_new,
        meta: {
            title: 'Linker.To - Home'
        }
    },
    {
        path: '/profile',
        name: "Profile",
        component: ProfileView,
        meta: {
            requiresAuth: true,
            title: 'Linker.To - Your Profile'
        }
    },
    {
        path: '/login',
        name: "Login",
        component: LoginView,
        meta: {
            title: 'Linker.To - Login'
        }
    },
    {
        path: '/change-password',
        name: "ChangePassword",
        component: ChangePasswordView,
        meta: {
            title: 'Linker.To - Change Password'
        }
    },
    {
        path: '/password-reset/:resetMode',
        name: "PasswordReset",
        component: PasswordResetVue,
        props: true,
        meta: {
            title: 'Linker.To - Reset Password'
        }
    },
    {
        path: '/forget-password',
        name: "ForgetPassword",
        component: ForgotPasswordView,
        meta: {
            title: 'Linker.To - Forgot Password'
        }
    },
    {
        path: '/confirm-signup',
        name: "ConfirmSignup",
        component: ConfirmSignupCodeView,
        meta: {
            title: 'Linker.To - Confirm Signup'
        }
    },
    {
        path: '/about',
        name: "About",
        component: AboutView,
        meta: {
            title: 'Linker.To - About Us'
        }
    },
    {
        path: '/admin',
        name: "Admin",
        component: AdminView,
        meta: {
            requiresAuth: true,
            title: 'Linker.To - Admin Dashboard'
        }
    },
    {
        // Single dashboard listing both links and files as stacked sections.
        path: '/dashboard',
        name: "Dashboard",
        component: ShortLinkView,
        meta: {
            title: 'Linker.To - Dashboard'
        }
    },
    {
        path: '/dashboard/link/:slug',
        name: "LinkDetail",
        component: LinkDetailView,
        props: true,
        meta: {
            title: 'Linker.To - Link Analytics'
        }
    },
    {
        path: '/dashboard/paste/:pasteId',
        name: "PasteDetail",
        component: () => import('@/views/PasteDetailView.vue'),
        props: true,
        meta: {
            title: 'Linker.To - Paste Details'
        }
    },
    // Legacy tab URLs → the unified dashboard.
    { path: '/dashboard/links', redirect: '/dashboard' },
    { path: '/dashboard/files', redirect: '/dashboard' },
    // Legacy paths → new dashboard URLs.
    { path: '/short-links', redirect: '/dashboard' },
    { path: '/short-links/:slug', redirect: to => `/dashboard/link/${to.params.slug}` },
    // ---- Tools (create surfaces) ----
    {
        path: '/shorten',
        name: "Shorten",
        component: ShortenView,
        meta: { title: 'Linker.To - Shorten a link' }
    },
    {
        path: '/share',
        name: "Share",
        component: FileUploadView,
        meta: { title: 'Linker.To - Share a file' }
    },
    {
        path: '/paste',
        name: "Paste",
        component: PasteView,
        meta: { title: 'Linker.To - Paste bin' }
    },
    // Legacy /files → the Share tool.
    { path: '/files', redirect: '/share' },
    {
        // Canonical public paste page (no auth — mirrors /files/:id).
        path: '/p/:pasteId',
        name: "PastePublic",
        component: () => import('@/views/PastePublicView.vue'),
        props: true,
        meta: {
            title: 'Linker.To - Paste'
        }
    },
    {
        path: '/files/:id',
        name: "FileDetails",
        component: FileDetailView,
        meta: {
            title: 'Linker.To - File Details'
        }
    },
    {
        path: '/auth-loading',
        name: 'AuthLoading',
        component: () => import('@/views/AuthLoadingView.vue'),
        meta: {
            title: 'Linker.To - Loading'
        }
      },
    {
        path: '/terms-of-service',
        name: "TermsOfService",
        component: TermsOfServiceView,
        meta: {
            title: 'Linker.To - Terms of Service'
        }
    },
    {
        path: '/not-authorized',
        name: "NotAuthorized",
        component: NotAuthorizedView,
        meta: {
            title: 'Linker.To - Not Authorized'
        }
    },
    {
        path: '/not-found',
        name: "FourZeroFour",
        component: FourZeroFourView,
        meta: {
            title: 'Linker.To - Page Not Found'
        }
    },
    {
        path: '/link/:slug',
        name: 'PublicLink',
        component: () => import('@/views/PublicLinkView.vue'),
        meta: {
            title: 'Linker.To - Short Link'
        }
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: {name: 'FourZeroFour'},
        meta: {
            title: 'Linker.To - Page Not Found'
        }
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
})

router.beforeEach(async (to, from, next) => {
    // Set the page title
    const pageTitle = to.meta.title || 'Linker.To';
    document.title = pageTitle;

    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

    if (requiresAuth) {
        // Wait for the initial session restore to settle before deciding, so a
        // cold load of a protected route doesn't bounce to login prematurely.
        await authReadyPromise;
        if (store.getters['auth/isAuthenticated']) {
            next();
        } else {
            next({ name: 'Login', query: { redirect: to.fullPath } });
        }
    } else {
        // No auth required, proceed immediately (public pages never wait on auth).
        next();
    }
});

export default router